import React from 'react';
import { Edit, X, Image, Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ImageUploadButton } from './ImageUploadButton';

export const ProductEditModal = ({
  product,
  form,
  setForm,
  saving,
  onClose,
  onSave,
}) => {
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setSizeField = (idx, field, value) => {
    const newSizes = [...form.sizes];
    const parsed = field === 'quantity' || field === 'price' ? parseFloat(value) || 0 : value;
    newSizes[idx] = { ...newSizes[idx], [field]: parsed };
    setForm((prev) => ({ ...prev, sizes: newSizes }));
  };

  const setImage = (idx, value) => {
    const newImages = [...form.images];
    newImages[idx] = value;
    setForm((prev) => ({ ...prev, images: newImages }));
  };

  const addImage = () => setForm((prev) => ({ ...prev, images: [...prev.images, ''] }));
  const removeImage = (idx) =>
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const addSize = () =>
    setForm((prev) => ({
      ...prev,
      sizes: [...(prev.sizes || []), { size: '', price: 0, quantity: 0, image: '' }],
    }));

  const removeSize = (idx) =>
    setForm((prev) => ({
      ...prev,
      sizes: (prev.sizes || []).filter((_, i) => i !== idx),
    }));

  const isNewProduct = !product?.id || product.id === 0;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="bg-purple-900 border-purple-600 max-w-4xl w-full my-8">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-purple-900 z-10">
          <CardTitle className="text-white flex items-center gap-2">
            {isNewProduct ? <Plus className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
            {isNewProduct ? 'Add New Product' : `Edit Product: ${product.name}`}
          </CardTitle>
          <Button variant="ghost" onClick={onClose} data-testid="close-product-modal">
            <X className="w-5 h-5 text-purple-300" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-purple-200">Product Name</Label>
              <Input
                value={form.name || ''}
                onChange={(e) => setField('name', e.target.value)}
                className="bg-purple-800 border-purple-600 text-white"
              />
            </div>
            <div>
              <Label className="text-purple-200">Category</Label>
              <Input
                value={form.category || ''}
                onChange={(e) => setField('category', e.target.value)}
                className="bg-purple-800 border-purple-600 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-purple-200">Base Price ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.basePrice || ''}
              onChange={(e) => setField('basePrice', parseFloat(e.target.value))}
              className="bg-purple-800 border-purple-600 text-white w-40"
            />
          </div>

          <div>
            <Label className="text-purple-200">Description</Label>
            <Textarea
              value={form.description || ''}
              onChange={(e) => setField('description', e.target.value)}
              className="bg-purple-800 border-purple-600 text-white min-h-[100px]"
            />
          </div>

          <div>
            <Label className="text-purple-200 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Main Product Image
            </Label>
            <div className="flex gap-2">
              <Input
                value={form.image || ''}
                onChange={(e) => setField('image', e.target.value)}
                placeholder="Paste image URL or use Upload"
                className="bg-purple-800 border-purple-600 text-white flex-1"
              />
              <ImageUploadButton
                onUploaded={(dataUrl) => setField('image', dataUrl)}
                label="Upload"
                data-testid="upload-main-image"
              />
            </div>
            {form.image && (
              <img
                src={form.image}
                alt="Main preview"
                className="w-24 h-24 object-cover rounded mt-2"
              />
            )}
          </div>

          <div>
            <Label className="text-purple-200 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Gallery Images
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={addImage}
                  className="bg-purple-700 hover:bg-purple-600"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add URL
                </Button>
                <ImageUploadButton
                  onUploaded={(dataUrl) =>
                    setForm((prev) => ({ ...prev, images: [...(prev.images || []), dataUrl] }))
                  }
                  label="Upload"
                  data-testid="upload-gallery-image"
                />
              </div>
            </Label>
            <div className="space-y-2 mt-2">
              {form.images?.map((img, idx) => (
                <div key={`gallery-${idx}`} className="flex items-center gap-2">
                  {img && (
                    <img src={img} alt={`Gallery ${idx}`} className="w-12 h-12 object-cover rounded" />
                  )}
                  <Input
                    value={img}
                    onChange={(e) => setImage(idx, e.target.value)}
                    placeholder="Image URL"
                    className="bg-purple-800 border-purple-600 text-white flex-1"
                  />
                  <ImageUploadButton
                    onUploaded={(dataUrl) => setImage(idx, dataUrl)}
                    label="Replace"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => removeImage(idx)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-purple-200 flex items-center justify-between">
              <span>Size Variations (Price, Stock &amp; Image)</span>
              <Button
                type="button"
                size="sm"
                onClick={addSize}
                className="bg-purple-700 hover:bg-purple-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Size
              </Button>
            </Label>
            <div className="space-y-4 mt-2">
              {form.sizes?.map((size, idx) => (
                <div
                  key={`size-${idx}`}
                  className="bg-purple-800/50 p-4 rounded-lg border border-purple-700"
                >
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-purple-300 text-sm">Size</Label>
                      <Input
                        value={size.size || ''}
                        onChange={(e) => setSizeField(idx, 'size', e.target.value)}
                        placeholder="e.g. 2oz Bottle"
                        className="bg-purple-700 border-purple-600 text-white w-36"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-purple-300 text-sm">Price $</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={size.price}
                        onChange={(e) => setSizeField(idx, 'price', e.target.value)}
                        className="bg-purple-700 border-purple-600 text-white w-24"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-purple-300 text-sm">Stock</Label>
                      <Input
                        type="number"
                        value={size.quantity}
                        onChange={(e) => setSizeField(idx, 'quantity', e.target.value)}
                        className="bg-purple-700 border-purple-600 text-white w-20"
                      />
                    </div>
                    {form.sizes.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => removeSize(idx)}
                        aria-label="Remove size"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {size.image && (
                      <img
                        src={size.image}
                        alt={size.size}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <Input
                      value={size.image || ''}
                      onChange={(e) => setSizeField(idx, 'image', e.target.value)}
                      placeholder="Size-specific image URL"
                      className="bg-purple-700 border-purple-600 text-white flex-1"
                    />
                    <ImageUploadButton
                      onUploaded={(dataUrl) => setSizeField(idx, 'image', dataUrl)}
                      label="Upload"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={onSave}
            disabled={saving}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-bold"
            data-testid="save-product-btn"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isNewProduct ? 'Create Product' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
